import { trackDocumentStructure } from "../tiptap/document_delivery_bridge"
import { Controller } from "stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import ImageUpload from "../tiptap/extensions/image_upload"
import AiHighlight from "../tiptap/extensions/ai_highlight"
import Code from "@tiptap/extension-code"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { TableCell } from "@tiptap/extension-table-cell"
import { TableHeader } from "@tiptap/extension-table-header"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import { SearchHighlight } from "../tiptap/extensions/search_highlight"
import CommentHighlight from "../tiptap/extensions/comment_highlight"
import { addCommentToMenu, wireCommentHighlightClicks } from "../tiptap/comments"
import { updateToolbarState } from "../tiptap/toolbar"
import { revealSection } from "../tiptap/section_navigation"
import {
  buildBubbleMenuElement,
  wireAiDropdown,
  restorePersistedHighlights,
  highlightActiveAiControls,
} from "../tiptap/ai_actions"

// Per-section Tiptap editor for sectioned documents.
// Each section gets its own editor instance (~100-400 nodes).
// Saves independently via fetch PATCH, not through the main form.
export default class extends Controller {
  static targets = ["bodyField", "editorMount", "sidebar", "deleteBtn"]
  static values = { sectionId: String, postId: String, position: Number }

  connect() {
    this._selectionThrottle = false
    this.wordCount = 0
    this.buildBubbleMenu()
    this.initEditor()
    if (this._lastSavedBody === undefined) this._lastSavedBody = this.editor.getHTML()
    this.registerWithParent()
    this._beforeUnload = event => {
      this.syncToField()
      if (this._savePromise || this.bodyFieldTarget.value !== this._lastSavedBody) {
        event.preventDefault()
        event.returnValue = ""
        this.saveSection(true)
      }
    }
    window.addEventListener("beforeunload", this._beforeUnload)

    // If URL hash targets this section, scroll to it and focus
    const targetId = window.location.hash.replace("#", "")
    if (targetId && this.element.id === targetId) {
      this._focusTimeout = setTimeout(() => {
        revealSection(this.element)
      }, 600)
    }
  }

  disconnect() {
    clearTimeout(this._syncDebounce)
    clearTimeout(this._saveTimeout)
    clearTimeout(this._highlightTimeout)
    clearTimeout(this._focusTimeout)
    cancelAnimationFrame(this._selectionFrame)
    this._selectionThrottle = false
    window.removeEventListener("beforeunload", this._beforeUnload)
    if (this.editor) {
      this.syncToField()
      if (!this._deleting) this.saveSection(true)
      this.editor.destroy()
    }
    const parent = this._parentElement || this.element
    parent.dispatchEvent(new CustomEvent("section:unregistered", {
      detail: { sectionId: this.sectionIdValue, editor: this.editor }, bubbles: true,
    }))
    if (this.bubbleEl) this.bubbleEl.remove()
    document.removeEventListener("mousedown", this._hideBubble)
  }

  buildBubbleMenu() {
    this.bubbleEl = buildBubbleMenuElement()
    this.bubbleEl.style.position = "fixed"
    this.bubbleEl.style.display = "none"
    this.bubbleEl.style.zIndex = "1000"

    this._aiCtx = {
      editor: null,
      editorEl: this.editorMountTarget,
      containerEl: this.element.closest(".editor-content"),
      syncToField: () => this.syncToField(),
    }
    wireAiDropdown(this.bubbleEl, () => {
      this._aiCtx.editor = this.editor
      return this._aiCtx
    })
    addCommentToMenu(this.bubbleEl, () => ({ editor: this.editor, editorEl: this.editorMountTarget }))

    // Hide bubble when clicking outside
    this._hideBubble = (e) => {
      if (this.bubbleEl && !this.bubbleEl.contains(e.target)) {
        this.bubbleEl.style.display = "none"
      }
    }
    document.addEventListener("mousedown", this._hideBubble)
  }

  positionBubbleMenu() {
    if (!this.editor || !this.bubbleEl) return

    const { from, to } = this.editor.state.selection
    if (from === to || window._tiptapAiProcessing || this.editor.state.selection.node) {
      this.bubbleEl.style.display = "none"
      return
    }

    // Don't show inside AI highlights
    const resolved = this.editor.state.doc.resolve(from)
    if (resolved.marks().some(m => m.type.name === "aiHighlight")) {
      this.bubbleEl.style.display = "none"
      return
    }

    // Use native selection rect for positioning
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      this.bubbleEl.style.display = "none"
      return
    }

    const range = sel.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) {
      this.bubbleEl.style.display = "none"
      return
    }

    this.bubbleEl.style.display = "flex"
    const menuWidth = this.bubbleEl.offsetWidth || 46
    const menuHeight = this.bubbleEl.offsetHeight || 46

    // Center above the selection
    let left = rect.left + (rect.width / 2) - (menuWidth / 2)
    let top = rect.top - menuHeight - 8

    // Keep within viewport
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))
    if (top < 8) top = rect.bottom + 8

    this.bubbleEl.style.left = left + "px"
    this.bubbleEl.style.top = top + "px"
  }

  initEditor() {
    const initialContent = this.bodyFieldTarget.value || ""

    const extensions = [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        code: false,
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder: `Section ${this.positionValue + 1}...` }),
      ImageUpload.configure({ inline: false, allowBase64: false }),
      AiHighlight,
      Code,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Subscript,
      Superscript,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      SearchHighlight,
      CommentHighlight,
    ]

    this.editor = new Editor({
      element: this.editorMountTarget,
      extensions,
      content: initialContent,
      editorProps: {
        attributes: {
          class: "editable body section-editor-instance",
          "aria-label": `Section ${this.positionValue + 1}`,
        },
      },
      onUpdate: ({ editor }) => {
        document.dispatchEvent(new CustomEvent("document-save:changed", { detail: { key: `section:${this.sectionIdValue}` } }))
        this.scheduleToolbarUpdate()
        clearTimeout(this._syncDebounce)
        this._syncDebounce = setTimeout(() => {
          this.syncToField()
          this.scheduleSave()
          this.checkEmpty()
          document.dispatchEvent(new CustomEvent("tiptap:content-changed"))
        }, 500)
      },
      onFocus: () => {
        this.element.dispatchEvent(new CustomEvent("section:focused", {
          detail: { sectionId: this.sectionIdValue, editor: this.editor },
          bubbles: true,
        }))
      },
      onSelectionUpdate: () => this.scheduleToolbarUpdate(),
      onCreate: ({ editor }) => {
        this._aiCtx.editor = editor
        this.updateWordCount()
        this.checkEmpty()
        this._highlightTimeout = setTimeout(() => {
          restorePersistedHighlights(editor, this.editorMountTarget, this._aiCtx)
          wireCommentHighlightClicks(this.editorMountTarget)
        }, 500)
      },
    })
  }

  scheduleToolbarUpdate() {
    if (this._selectionThrottle) return
    this._selectionThrottle = true
    this._selectionFrame = requestAnimationFrame(() => {
      const toolbar = document.querySelector(".tiptap-fixed-toolbar")
      if (toolbar && window.tiptapEditor === this.editor) {
        updateToolbarState(toolbar, this.editor)
      }
      highlightActiveAiControls(this.editorMountTarget)
      this.positionBubbleMenu()
      this._selectionThrottle = false
    })
  }

  registerWithParent() {
    this._parentElement = this.element.closest('[data-controller~="section-editor"]')
    this.element.dispatchEvent(new CustomEvent("section:registered", {
      detail: { sectionId: this.sectionIdValue, controller: this },
      bubbles: true,
    }))
  }

  checkEmpty() {
    if (!this.editor) return
    const isEmpty = this.editor.state.doc.textContent.trim().length === 0
    this.element.classList.toggle("section-empty", isEmpty && !this.editor.isFocused)
  }

  syncToField() {
    if (!this.editor) return
    let html = this.editor.getHTML()
    html = html.replace(/ data-chnode="[^"]*"/g, "")
    this.bodyFieldTarget.value = html
    this.updateWordCount()
  }

  updateWordCount() {
    if (!this.editor) return
    const text = this.editor.state.doc.textContent
    this.wordCount = text.trim() ? text.trim().split(/\s+/).length : 0
    this.element.dispatchEvent(new CustomEvent("section:wordcount", {
      detail: { sectionId: this.sectionIdValue, count: this.wordCount },
      bubbles: true,
    }))
  }

  scheduleSave() {
    clearTimeout(this._saveTimeout)
    this._saveTimeout = setTimeout(() => this.saveSection(), 1500)
  }

  saveSection(keepalive = false) {
    const delivery = document.getElementById("publishForm")?._documentDelivery
    if (delivery && !delivery.disposed) {
      const pending = delivery.saveSection(this, keepalive)
      this._savePromise = pending
      pending.finally(() => { if (this._savePromise === pending) this._savePromise = null })
      return pending
    }
    if (this._savePromise) return this._savePromise.then(() => this.saveSection(keepalive))
    const body = this.bodyFieldTarget.value
    if (this._lastSavedBody === body) return

    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = "Saving..."
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this._savePromise = fetch(`/posts/${this.postIdValue}/sections/${this.sectionIdValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
      },
      body: JSON.stringify({ post_section: { body: body } }),
      keepalive,
      credentials: "same-origin",
    })
    .then(res => {
      if (res.ok) {
        this._lastSavedBody = body
        if (window.autoSaveSucceeded) window.autoSaveSucceeded()
      } else {
        if (window.autoSaveFailed) window.autoSaveFailed()
      }
    })
    .catch(() => {
      if (window.autoSaveFailed) window.autoSaveFailed()
    })
    .finally(() => { this._savePromise = null })
    return this._savePromise
  }

  addSectionBelow() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return trackDocumentStructure("Adding a section", () => fetch(`/posts/${this.postIdValue}/sections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
      },
      body: JSON.stringify({ after_position: this.positionValue }),
      credentials: "same-origin",
    })
    .then(res => res.json())
    .then(data => {
      if (data.status === "success" && data.html) {
        const currentFrame = this.element.closest("turbo-frame")
        if (currentFrame) {
          currentFrame.insertAdjacentHTML("afterend", data.html)
          const newFrame = currentFrame.nextElementSibling
          if (newFrame) {
            setTimeout(() => {
              revealSection(newFrame)
            }, 300)
          }
        }
      }
    })
    .catch(err => console.error("Failed to add section:", err)))
  }

  deleteSection() {
    if (!confirm("Delete this section? This cannot be undone.")) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return trackDocumentStructure("Deleting a section", () => fetch(`/posts/${this.postIdValue}/sections/${this.sectionIdValue}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
      },
      credentials: "same-origin",
    })
    .then(res => {
      if (res.ok) {
        this._deleting = true
        const frame = this.element.closest("turbo-frame")
        if (frame) frame.remove()
        else this.element.remove()
      } else {
        alert("Failed to delete section.")
      }
    })
    .catch(() => alert("Failed to delete section.")))
  }

  moveUp() { this._moveSection("up") }
  moveDown() { this._moveSection("down") }

  _moveSection(direction) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return trackDocumentStructure("Reordering sections", () => fetch(`/posts/${this.postIdValue}/sections/${this.sectionIdValue}/move`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json",
      },
      body: JSON.stringify({ direction }),
      credentials: "same-origin",
    })
    .then(res => {
      if (res.ok) {
        const frame = this.element.closest("turbo-frame")
        if (!frame) return

        if (direction === "up") {
          const prev = frame.previousElementSibling
          if (prev) frame.parentNode.insertBefore(frame, prev)
        } else {
          const next = frame.nextElementSibling
          if (next) frame.parentNode.insertBefore(next, frame)
        }
      }
    })
    .catch(() => alert("Failed to move section.")))
  }
}
